/**
 * Merge managed lang entries into an imported base `.lang` file.
 *
 * "Do not replace the lang file, add to it": when a project was imported, export
 * must preserve every line of the original lang file (comments, block/entity/UI
 * translations this tool doesn't model) and only override the specific keys the
 * generator manages, appending any managed keys the base didn't already have.
 */

export interface LangEntry {
  key: string;
  value: string;
}

const MANAGED_HEADER = "## --- Added by Guidebook Generator ---";

/** Escape a value so it stays on a single `.lang` line. */
function escapeValue(value: string): string {
  return value.replace(/\r?\n/g, "\\n");
}

/** Render managed entries as a fresh `key=value` block (no base to merge into). */
export function renderLangEntries(entries: LangEntry[]): string {
  return entries.map((e) => `${e.key}=${escapeValue(e.value)}`).join("\n") + "\n";
}

/**
 * Merge `entries` into `baseRaw`:
 *  - a base line `key=value` whose key is managed is replaced in place with the
 *    managed value (first occurrence wins; later duplicates of that key are
 *    dropped so no stale value lingers),
 *  - every other base line (comments, blank lines, unmanaged keys) is kept
 *    verbatim and in order,
 *  - managed keys not present in the base are appended under a header.
 */
export function mergeLang(baseRaw: string, entries: LangEntry[]): string {
  const managed = new Map<string, string>();
  for (const e of entries) managed.set(e.key, escapeValue(e.value));

  const emitted = new Set<string>();
  const out: string[] = [];

  for (const rawLine of baseRaw.split(/\r?\n/)) {
    const trimmed = rawLine.trim();
    // Preserve comments and blanks untouched.
    if (!trimmed || trimmed.startsWith("#")) {
      out.push(rawLine);
      continue;
    }
    const eq = rawLine.indexOf("=");
    if (eq === -1) {
      out.push(rawLine);
      continue;
    }
    const key = rawLine.slice(0, eq).trim();
    if (managed.has(key)) {
      if (!emitted.has(key)) {
        out.push(`${key}=${managed.get(key)}`);
        emitted.add(key);
      }
      // Drop duplicate base definitions of a managed key.
      continue;
    }
    out.push(rawLine);
  }

  // Trim a single trailing blank line so the appended block sits cleanly.
  while (out.length > 0 && out[out.length - 1].trim() === "") out.pop();

  const remaining = entries.filter((e) => !emitted.has(e.key));
  if (remaining.length > 0) {
    out.push("");
    out.push(MANAGED_HEADER);
    for (const e of remaining) out.push(`${e.key}=${managed.get(e.key)}`);
  }

  return out.join("\n") + "\n";
}
